import React, {useEffect} from 'react';
import {useRef, useState} from 'react';

import {Autocomplete, type AutocompleteRenderInputParams} from '@mui/material';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import type {Theme} from '@netx/core-components/Theme';

import type {AttributeAttributes} from '@netx/core-asset/models/attribute';
import {metadataDatasetAutoList} from '../../../../api';

import useUpdateEffect from '@netx/core-components/hooks/useUpdateEffect';
import {isEmptyValue} from '@netx/core-components/Form';

const styles = (theme: Theme) =>
	createStyles({
		root: {
			width: '100%',
			position: 'relative',
		},
		labelIcon: {
			fontSize: '1em',
			marginRight: 8,
		},
		labelWrapper: {
			alignItems: 'center',
			display: 'inline-flex',
			flex: '0 0 auto',
		},
	});

export interface LookupProps {
	attrData: AttributeAttributes;
	disabled?: boolean;
	onChange?: (value: string | null | undefined) => void;
	onLookup: (lookupValue: string) => void;
	renderInput: (params: AutocompleteRenderInputParams) => React.ReactNode;
	value?: string;
}

type Option = string;

const Lookup = ({
	classes,

	attrData,
	disabled = false,
	onChange,
	onLookup,
	renderInput,
	value,
}: WithStyles<typeof styles> & LookupProps) => {
	const defaultValue: string = !isEmptyValue(value)
		? Array.isArray(value)
			? value[0]
			: value
		: '';

	const [isLoading, setIsLoading] = useState(false);
	const [options, setOptions] = useState<Option[]>([]);
	const [textValue, setTextValue] = useState<string | undefined>(defaultValue);

	const lookupRef = useRef('');

	useEffect(() => {
		lookupRef.current = '';
	}, []);

	useUpdateEffect(() => {
		setTextValue(Array.isArray(value) ? value[0] : value);
	}, [value]);

	const testLookup = (lookupValue: string) => {
		// TODO: We can attach listeners to the list component and deduce actual selection
		// I am making this behave as it does in core at this moment
		if (options.includes(lookupValue)) {
			if (lookupRef.current !== lookupValue) {
				lookupRef.current = lookupValue;
				onLookup(lookupValue);
				// Wipe out options otherwise they menu will open when we change the value
				// programatically (if the new  value does not match all available options)
				setOptions([]);
				return true;
			}
		}

		lookupRef.current = '';
		return false;
	};

	const autoList = async (lookupString: string) => {
		setOptions([]);

		if (lookupString == null || !lookupString.length) return;

		setIsLoading(true);
		const results = await metadataDatasetAutoList({
			id: attrData.attributeid,
			value: lookupString,
		});
		setIsLoading(false);

		setOptions(results);
		testLookup(lookupString);
	};

	const handleInputChange = (event: React.SyntheticEvent, str: string) => {
		if (!event || disabled) return;

		setTextValue(str);
		onChange?.(str);

		if (
			event.type === 'click' ||
			(event.type === 'keydown' &&
				(event as React.KeyboardEvent).key === 'Enter')
		) {
			testLookup(str);
		} else {
			!testLookup(str) && autoList(str);
		}
	};

	return (
		<Autocomplete<Option, false, true, true>
			className={classes.root}
			freeSolo
			autoComplete
			autoHighlight
			blurOnSelect
			disableClearable
			loading={isLoading}
			id="autocomplete-text"
			disabled={disabled}
			inputValue={textValue}
			onChange={handleInputChange}
			onInputChange={handleInputChange}
			options={options}
			renderInput={renderInput}
			value={defaultValue}
		/>
	);
};

export default withStyles(styles, {
	name: 'Attributes-Attribute-Lookup',
})(Lookup);
